/*
 * @license
 * Copyright Akveo. All Rights Reserved.
 * Licensed under the MIT License. See License.txt in the project root for license information.
 */

import { Injectable } from '@angular/core';
import {
  NbAuthResult,
  NbAuthStrategyClass,
  NbAuthStrategyOptions,
} from '@nebular/auth';
import { from, Observable, of as observableOf } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import {
  authState,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  User,
} from '@angular/fire/auth';
import { NbFirebaseBaseStrategy } from '../base/firebase-base.strategy';
import {
  firebasePasswordStrategyOptions,
  NbFirebasePasswordStrategyOptions,
} from './firebase-password-strategy.options';

@Injectable()
export class NbFirebasePasswordStrategy extends NbFirebaseBaseStrategy {
  protected override defaultOptions: NbFirebasePasswordStrategyOptions =
    firebasePasswordStrategyOptions;

  static setup(
    options: NbFirebasePasswordStrategyOptions,
  ): [NbAuthStrategyClass, NbAuthStrategyOptions] {
    return [NbFirebasePasswordStrategy, options];
  }

  authenticate({ email, password }: any): Observable<NbAuthResult> {
    const module = 'login';
    return from(
      this.inContext(() =>
        signInWithEmailAndPassword(this.afAuth, email, password),
      ),
    ).pipe(
      switchMap((res) => this.processSuccess(res, module)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  override refreshToken(): Observable<NbAuthResult> {
    const module = 'refreshToken';
    return this.inContext(() => authState(this.afAuth)).pipe(
      take(1),
      switchMap((user) => {
        if (user == null) {
          return observableOf(
            new NbAuthResult(
              false,
              null,
              this.getOption(`${module}.redirect.failure`),
              [
                "There is no logged in user so refresh of id token isn't possible",
              ],
            ),
          );
        }
        return this.refreshIdToken(user, module);
      }),
    );
  }

  override register({ email, password }: any): Observable<NbAuthResult> {
    const module = 'register';
    return from(
      this.inContext(() =>
        createUserWithEmailAndPassword(this.afAuth, email, password),
      ),
    ).pipe(
      switchMap((res) => this.processSuccess(res, module)),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  override requestPassword({ email }: any): Observable<NbAuthResult> {
    const module = 'requestPassword';
    return from(
      this.inContext(() => sendPasswordResetEmail(this.afAuth, email)),
    ).pipe(
      map(() => {
        return new NbAuthResult(
          true,
          null,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption(`${module}.defaultMessages`),
        );
      }),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  override resetPassword({
    code,
    password,
  }: {
    code: string;
    password: string;
  }): Observable<NbAuthResult> {
    const module = 'resetPassword';
    return from(
      this.inContext(() => confirmPasswordReset(this.afAuth, code, password)),
    ).pipe(
      map(() => {
        return new NbAuthResult(
          true,
          null,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption(`${module}.defaultMessages`),
        );
      }),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  protected updatePassword(
    user: { updatePassword: (arg0: any) => any },
    password: any,
    module: string,
  ) {
    return from(user.updatePassword(password)).pipe(
      map((token) => {
        return new NbAuthResult(
          true,
          null,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption(`${module}.defaultMessages`),
          this.createToken(token),
        );
      }),
      catchError((error) => this.processFailure(error, module)),
    );
  }

  protected refreshIdToken(
    user: User,
    module: string,
  ): Observable<NbAuthResult> {
    return from(user.getIdToken(true)).pipe(
      map((token) => {
        return new NbAuthResult(
          true,
          null,
          this.getOption(`${module}.redirect.success`),
          [],
          this.getOption(`${module}.defaultMessages`),
          this.createToken(token),
        );
      }),
      catchError((error) => this.processFailure(error, module)),
    );
  }
}
